import dayjs from "dayjs";
import { find, get, isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAdd, MdCheckCircle, MdDeleteOutline } from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import format from "string-template";
import { CONTENT_TYPES, DATE_FORMAT, DOCUMENTS_TYPES } from "utils/constants";

const getLatestValidSignMoment = (option) => {
  const todayStart = moment().startOf("day");
  if (!option) {
    return todayStart;
  }
  const issuanceDate = get(option, "issuanceDate");
  const deltaStart = get(option, "deltaStart");
  if (issuanceDate == null || deltaStart == null) {
    return todayStart;
  }
  const businessLatest = moment(issuanceDate)
    .startOf("day")
    .subtract(deltaStart, "days");
  return moment.min([todayStart, businessLatest]);
};

const Initial = ({
  onSign,
  options,
  setSelectedSubscription,
  selectedSubscription,
  manualLoading,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const SIGN_PROJECT = t("LIST.SIGN_PROJECT", { returnObjects: true });
  const [signedDocuments, setSignedDocuments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [attachments, setAttachments] = useState({});
  const [errors, setErrors] = useState("");
  const [signDatePickerKey, setSignDatePickerKey] = useState(0);

  const selectedOption = useMemo(
    () => find(options, { value: selectedSubscription }),
    [options, selectedSubscription],
  );

  const allRequiredAttachmentsUploaded = useMemo(() => {
    const documentKeys = Object.keys(signedDocuments);
    if (documentKeys.length === 0) {
      return false;
    }
    return documentKeys.every(
      (document) => !isEmpty(attachments[DOCUMENTS_TYPES[document]]),
    );
  }, [attachments, signedDocuments]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const onFileRemove = async ({ uid: id, key }) => {
    setAttachments((prev) => ({
      ...prev,
      [key]: [...prev[key]]?.filter((file) => file.uid !== id),
    }));
  };
  useEffect(() => {
    const issuanceDate = get(selectedOption, "issuanceDate");
    const deltaStart = get(selectedOption, "deltaStart");
    if (
      issuanceDate != null &&
      deltaStart != null &&
      selectedDate &&
      selectedDate
        .clone()
        .add(deltaStart, "days")
        .startOf("day")
        .isAfter(moment(issuanceDate).startOf("day"))
    ) {
      return setErrors(
        format(SIGN_PROJECT.SIGN_DATE_ERROR, {
          date: moment(issuanceDate)
            .subtract(deltaStart, "days")
            .format(DATE_FORMAT),
        }),
      );
    }
    setErrors("");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- SIGN_PROJECT from `t(..., { returnObjects })` is not referentially stable
  }, [options, selectedDate, selectedOption, selectedSubscription]);

  return (
    <div className="sign-project__container">
      <Select
        label={SIGN_PROJECT.CHOOSE_FORMULA}
        placeholder={SIGN_PROJECT.CHOOSE_PLACEHOLDER}
        type="subtext"
        options={options}
        onChange={(option) => {
          setSignedDocuments(
            get(find(options, { value: option }), "generatedDocuments", []),
          );
          setSelectedSubscription(option);
          setSelectedDate(null);
          setSignDatePickerKey(0);
        }}
      />

      <Input
        key={`sign-date-${selectedSubscription || "pending"}-${signDatePickerKey}`}
        type="date"
        format={DATE_FORMAT}
        value={selectedDate}
        label={SIGN_PROJECT.SIGN_DATE}
        placeholder={SIGN_PROJECT.SIGN_DATE}
        onChange={handleDateChange}
        defaultPickerValue={dayjs(
          getLatestValidSignMoment(selectedOption).toDate(),
        )}
        onOpenChange={(open) => {
          if (!open) {
            setSignDatePickerKey((key) => key + 1);
          }
        }}
        disabledDate={(current) => {
          if (!current) {
            return false;
          }
          if (current.clone().startOf("day").isAfter(moment().startOf("day"))) {
            return true;
          }
          const issuanceDate = get(selectedOption, "issuanceDate");
          const deltaStart = get(selectedOption, "deltaStart");
          if (issuanceDate == null || deltaStart == null) {
            return false;
          }
          return current
            .clone()
            .add(deltaStart, "days")
            .startOf("day")
            .isAfter(moment(issuanceDate).startOf("day"));
        }}
        touched
        errors={errors}
      />
      <div className="divider" />
      {Object.keys(signedDocuments).length > 0 && (
        <>
          <div className="sign-project__attachments--title">
            <h4>{SIGN_PROJECT.ATTACHMENTS}</h4>
            <span>{SIGN_PROJECT.ATTACHMENTS_SIZE}</span>
          </div>
          {Object.keys(signedDocuments).map((document) => (
            <div key={`document-${document}`}>
              <div className="sign-project__attachments">
                <p className="sign-project__attachments--label">
                  <span className="sign-project__attachments--name">
                    {SIGN_PROJECT[document]}
                  </span>
                  <span className="sign-project__attachments--required">
                    {SIGN_PROJECT.ATTACHMENT_REQUIRED}
                  </span>
                </p>
                {!isEmpty(attachments[DOCUMENTS_TYPES[document]]) && (
                  <div className="sign-project__attachments--list">
                    {attachments[DOCUMENTS_TYPES[document]].map((file) => (
                      <div
                        className="sign-project__attachments--item"
                        key={file?.uid}
                      >
                        <div className="sign-project__attachments--item--left">
                          <MdCheckCircle color={COLORS.C_SUCCESS} size={16} />
                          <p>{file?.name}</p>
                          <span>{`(${(file?.size / 1024).toFixed(
                            1,
                          )} KB)`}</span>
                        </div>
                        <div
                          className="sign-project__attachments--item--delete"
                          onClick={() =>
                            onFileRemove({
                              uid: file?.uid,
                              key: DOCUMENTS_TYPES[document],
                            })
                          }
                        >
                          <MdDeleteOutline size={16} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Input
                  type="file"
                  variant="uploader"
                  listType="text"
                  onUpload={(fileList) => {
                    setAttachments((prev) => ({
                      ...prev,
                      [DOCUMENTS_TYPES[document]]: fileList,
                    }));
                  }}
                  validTypes={[
                    CONTENT_TYPES.PNG,
                    CONTENT_TYPES.JPEG,
                    CONTENT_TYPES.JPG,
                    CONTENT_TYPES.PDF,
                  ]}
                  fileList={attachments[DOCUMENTS_TYPES[document]]}
                  showUploadList={false}
                >
                  <Button type="primary--link">
                    <MdAdd size={12} />
                    {SIGN_PROJECT.ADD_FILES}
                  </Button>
                </Input>
              </div>
              <div className="divider" />
            </div>
          ))}
        </>
      )}

      <div className="m-top-24" />
      <Button
        type="primary"
        size="full--width"
        onClick={() =>
          onSign({
            subscriptionId: selectedSubscription,
            signedDate: selectedDate,
            attachments,
          })
        }
        disabled={
          !selectedDate ||
          !selectedSubscription ||
          !isEmpty(errors) ||
          manualLoading ||
          isEmpty(signedDocuments) ||
          !allRequiredAttachmentsUploaded
        }
      >
        {SIGN_PROJECT.SUBMIT}
      </Button>
    </div>
  );
};

export default Initial;
